import React, { useState, useEffect } from 'react';
import { CollapsibleList } from '..';
import { isNullOrEmptyArray } from '../../util/arrays';


import * as Ui from './styles';

/**
 * Componente responsável pela renderização de uma lista de itens collapsáveis
 * implementando o comportamento de selectAll e shift select
* <MultiCollapsibleList
    datas={[{label, checked, value}]}
    onSelect={handleSelect}
    listType={'collapsibleList'}
    withAllSelected
    onSelectAll={handleAllSelected}
    setActiveItems
  />
 */
function MultiCollapsibleListComp({
  /**
   * Define se o item todos selecionados sera exibido
   */
  withAllSelected = false,
  /**
   * Propaga o evento de quando o item é selecionado
   */
  onSelect,
  /**
   * Propaga o evento de quando o selectAll é selecionado
   */
  onSelectAll,
  /**
   * Propaga o evento de expansão do item
   */
  onExpand,
  /**
   * array que é percorrido para exibição dos itens colapsáveis {label: string : checked: boolean, value:'algo de valor unico', hidden: boolean}
   */
  datas,
  /**
   * Define se o campo selecionar todos esta marcado
   */
  allItemsSelected = false,
  /**
   * Define se os itens colapsáveis são expansíveis
   */
  expansible = false,
  /**
   * Define a altura do elemento
   */
  bodyHeight,
  /**
   * Define se a seta ira aparecer
   */
  withArrow = true,
}) {
  const [datasObj, setDatasObj] = useState(datas);
  const [isAllSelected, setIsAllSelected] = useState(allItemsSelected);
  // Ultimo item clickado da lista
  const [lastItemClicked, setLastItemClicked] = useState({});
  // Monitora a tecla shift
  const ref = React.useRef({});

  /**
   * Adicionando o evento de shiftKey pressed
   */
  useEffect(() => {
    /**
     * atualiza o atributo "isShiftPressed" associado à referência do componente
     * @param {*} evento de keydown|keyup
     */
    function updateRefIsShiftPressed(event) {
      ref.current.isShiftPressed = event.shiftKey;
    }

    document.addEventListener('keydown', updateRefIsShiftPressed);
    document.addEventListener('keyup', updateRefIsShiftPressed);

    /**
     * removendo os event listeners quando o componente for destruído,
     * evitando memory leak.
     */
    return () => {
      document.removeEventListener('keydown', updateRefIsShiftPressed);
      document.removeEventListener('keyup', updateRefIsShiftPressed);
    };
  }, []); // como a array de dependencias está vazia, este efeito será executado apenas quando o componente for montado

  /**
   * sempre que datas mudar, atualize o estado interno do MultiCollapsibleList,
   * a.k.a. datasObj
   */
  useEffect(() => {
    setDatasObj(datas);
  }, [datas]);

  const handleClickItemCallback = React.useCallback((checked, index) => {
    setLastItemClicked((_lastItemClicked) => {
      const maior = _lastItemClicked.index > index ? _lastItemClicked.index : index;
      const menor = _lastItemClicked.index < index ? _lastItemClicked.index : index;
      setDatasObj((data) => {
        let updatedData = [];
        if (ref.current.isShiftPressed) {
          updatedData = data.map((item, itemId) => {
            if (menor <= itemId && itemId <= maior) {
              if (checked) {
                if (!item.checked && !item.hidden) {
                  return {
                    ...item,
                    checked: true,
                  };
                }
              } else if (item.checked && !item.hidden) {
                return {
                  ...item,
                  checked: false,
                };
              }
            }
            return item;
          });
        } else {
          updatedData = [...data];
          updatedData[index].checked = checked;
        }
        const selectedItems = updatedData.filter((item) => item.checked);
        const allSelected = updatedData.length === selectedItems.length;
        if (onSelect) onSelect(selectedItems, allSelected);
        setIsAllSelected(allSelected);
        return updatedData;
      });
      return { checked, index };
    });
  }, [onSelect]);
  /**
   * lida com o click do selectAll
   */
  const handleSelectAllCallback = React.useCallback((checked) => {
    setIsAllSelected(checked);
    setDatasObj((_datasObj) => {
      const checkedDatasObj = [];
      const newDataObj = [];
      _datasObj.forEach((current) => {
        if ((!current.hidden && !checked)) {
          checkedDatasObj.slice(checkedDatasObj.indexOf(current, 1));
          newDataObj.push({
            ...current,
            checked: !checked,
          });
        } else if ((!current.hidden && checked) || current.checked) {
          checkedDatasObj.push({
            ...current,
            checked,
          });
          newDataObj.push({
            ...current,
            checked,
          });
        }
      });
      if (onSelectAll) {
        onSelectAll(checkedDatasObj, checked);
      }

      return newDataObj;
    });
  }, [onSelectAll]);

  useEffect(() => {
    setIsAllSelected(() => (!datasObj.some((item) => !item.checked && !item.hidden) && !isNullOrEmptyArray(datasObj)));
  }, [datasObj]);
  const handleExpandCallback = React.useCallback((item, index) => {
    if (onExpand) onExpand(item, index);
  }, [onExpand]);

  return (
    <Ui.Container>
      {withAllSelected && (
        <Ui.CollapsibleListWrapper>
          <CollapsibleList
            expansible={false}
            title={'Selecionar tudo'}
            checkedTitle={isAllSelected}
            onSelectTitle={handleSelectAllCallback}
            withArrow={false}
            isUpperCase={false}
          />
        </Ui.CollapsibleListWrapper>
      )}
      <Ui.BodySection bodyHeight={bodyHeight}>
        {datasObj.map((item, index) => (
          !item.hidden
          && (
            <Ui.CollapsibleListWrapper key={`key-${item.value}`}>
              <CollapsibleList
                index={index}
                id={item.value}
                expansible={expansible}
                title={item.label}
                checkedTitle={item.checked}
                onSelectTitle={handleClickItemCallback}
                onExpand={handleExpandCallback}
                highlightFocusItem
                data={item.data}
                withArrow={withArrow}
              />
            </Ui.CollapsibleListWrapper>
          )
        ))}
      </Ui.BodySection>
    </Ui.Container>
  );
}

export const MultiCollapsibleList = React.memo(MultiCollapsibleListComp);
